import { useEffect, useRef, useState, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { columnsConfig, columnsDefinition } from './utils';
import type { ResponseData } from './utils';
import { links, API } from '@http';

import { pullListSessions } from '@store/sessions/actions';
import { startOpenAccountLookup, stopOpenAccountLookup } from '@store/UIState/actions';
import { selectOpeningAccountsLookup } from '@store/UIState/selectors';
import { replace } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';

export const useListOfAccounts = (
	setError: Dispatch<SetStateAction<number>>,
	accountsData: ResponseData[],
	isLoading: boolean,
) => {
	const dispatch = useDispatch();

	const gridRef = useRef<HTMLCasGridElement | null>(null);
	const [isMounted, setIsMounted] = useState(false);
	const [redirectUrl, setRedirectUrl] = useState('');

	const isAccountLookupSessionLoading = useSelector(selectOpeningAccountsLookup);

	const openLookupSession = useCallback(
		async ({ AccountId, AccountName }: ResponseData) => {
			try {
				dispatch(startOpenAccountLookup());
				const { data } = await API.post(links.accountsSearch.openSession, {
					AccountId,
					AccountName,
				});

				if (!data) {
					dispatch(stopOpenAccountLookup());
					return;
				}

				const newRedirectUrl = `/sessions/${data.newLookupId}/${data.accountName}?SUMMARY`;

				dispatch(pullListSessions(true));
				setRedirectUrl(newRedirectUrl);
			} catch (e) {
				setError(e);
			}
		},
		[dispatch, setError],
	);

	useEffect(() => {
		const isRedirectUrlSettled = !!redirectUrl.length;

		if (isRedirectUrlSettled && !isAccountLookupSessionLoading) dispatch(replace(redirectUrl));
	}, [dispatch, isAccountLookupSessionLoading, redirectUrl]);

	useEffect(() => {
		const grid = gridRef.current;
		if (!grid) return;

		const numberOfAccounts = accountsData.length;

		if (!numberOfAccounts) {
			grid.style.height = '0px';
			return;
		}

		const headerHeightPx = 48;
		const scrollHeightPx = 18;
		const rowHeightPx = 64;
		const totalHeightPx = headerHeightPx + scrollHeightPx + rowHeightPx * numberOfAccounts;

		grid.style.height = `${totalHeightPx}px`;
	}, [accountsData]);

	useEffect(() => {
		if (!isMounted) return;
		gridRef?.current?.setRowData(accountsData);
	}, [accountsData, isMounted]);

	useEffect(() => {
		if (isMounted) return;

		(async function () {
			try {
				const grid = gridRef.current;

				columnsDefinition.forEach(async item => {
					const config = columnsConfig[item.identifier];
					if (config) await item.ref?.current?.setColumnDef(config(accountsData));

					if (item.actions) {
						await item.actions.ref?.current?.setCellDef({
							actionsGetter: ({ data }: { data: any }) => {
								return [
									{
										label: 'Open',
										actionHandler: () => openLookupSession(data),
									},
								];
							},
						});
					}
				});

				if (!grid) return;
				await grid.init({
					rowData: [],
				});
			} catch (e) {
				setError(e);
			} finally {
				setIsMounted(true);
			}
		})();
	}, [accountsData, isMounted, openLookupSession, setError]);

	return {
		gridRef,
		isLoading,
		columnsDefinition,
	};
};
